// SPDX-FileCopyrightText: 2017-2022 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

import React, { useContext, useState } from 'react'

import { isLoading } from 'lib-common/api'
import { useBoolean } from 'lib-common/form/hooks'
import type { ChildId } from 'lib-common/generated/api-types/shared'
import { CollapsibleContentArea } from 'lib-components/layout/Container'
import { H2 } from 'lib-components/typography'
import { Gap } from 'lib-components/white-space'

import { useTranslation } from '../../state/i18n'
import { renderResult } from '../async-rendering'
import PersonDetails from '../person-shared/PersonDetails'

import AdditionalInformation from './person-details/AdditionalInformation'
import type { ChildState } from './state'
import { ChildContext } from './state'

interface Props {
  id: ChildId
}

export default React.memo(function ChildDetails({ id }: Props) {
  const { i18n } = useTranslation()
  const { person, permittedActions } = useContext<ChildState>(ChildContext)

  const [open, setOpen] = useState(true)
  const [sensitiveDetailsOpen, { toggle: toggleSensitiveDetails }] =
    useBoolean(false)

  return (
    <div data-qa="person-details-section" data-isloading={isLoading(person)}>
      <CollapsibleContentArea
        title={<H2 $noMargin>{i18n.childInformation.personDetails.title}</H2>}
        open={open}
        toggleOpen={() => setOpen(!open)}
        $opaque
      >
        <Gap $size="s" />
        {renderResult(person, (person) => (
          <PersonDetails
            person={person}
            isChild={true}
            permittedActions={permittedActions}
            sensitiveDetailsOpen={sensitiveDetailsOpen}
            onToggleSensitiveDetails={toggleSensitiveDetails}
          />
        ))}
        {sensitiveDetailsOpen &&
          permittedActions.has('READ_ADDITIONAL_INFO') && (
            <div
              className="additional-information"
              data-qa="additional-information"
            >
              <AdditionalInformation childId={id} />
            </div>
          )}
      </CollapsibleContentArea>
    </div>
  )
})
